/**
 * Organizations Service (Frontend)
 * API calls för organisations med Closure Table
 */

import api from '../api/client';
import type {
  Organization,
  OrganizationWithChildren,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  MoveOrganizationRequest,
  DrivingLogSettings,
} from '@/types/organizations';
import type { EntityId } from '@/types/ids';

const assertPositiveIntId = (id: EntityId, fieldName: string = 'organizationId'): number => {
  const parsed = typeof id === 'number' ? id : Number(id);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    throw new Error(`Invalid ${fieldName}: expected positive integer`);
  }
  return parsed;
};

export const organizationsService = {
  /**
   * Hämta alla organisationer
   */
  getAllOrganizations: async (): Promise<Organization[]> => {
    const response = await api.get('/api/organizations');
    return response.data.data;
  },

  /**
   * Hämta alla organisationer med paginering
   */
  getAllOrganizationsPaginated: async (
    page: number = 1,
    pageSize: number = 50
  ): Promise<{
    organizations: Organization[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> => {
    const response = await api.get('/api/organizations/paginated', {
      params: { page, pageSize },
    });
    return response.data.data;
  },

  /**
   * Hämta toppnoder (för t.ex. "Skapa under organisation" som global admin)
   */
  getTopNodes: async (): Promise<Organization[]> => {
    const response = await api.get('/api/organizations/top-nodes');
    return response.data.data ?? [];
  },

  /**
   * OPTIMERING: Hämta top nodes med deras direct children i batch
   * Detta undviker N+1 query problemet
   */
  getTopNodesWithDirectChildren: async (
    page: number = 1,
    pageSize: number = 20,
    search: string = ''
  ): Promise<{
    topNodes: Organization[];
    directChildrenMap: Record<string, Organization[]>;
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> => {
    const response = await api.get('/api/organizations/top-nodes/with-children', {
      params: { page, pageSize, search },
    });
    return response.data.data;
  },

  /**
   * Hämta en organisation
   */
  getOrganizationById: async (id: EntityId): Promise<Organization> => {
    const safeId = assertPositiveIntId(id, 'id');
    const response = await api.get(`/api/organizations/${safeId}`);
    return response.data.data;
  },

  /** Toppnod för en organisation. Null om den inte kan avgöras. */
  getTopNodeId: async (id: EntityId): Promise<string | null> => {
    const safeId = assertPositiveIntId(id, 'id');
    const response = await api.get<{ success: boolean; data?: { topNodeOrganizationId?: string | null } }>(
      `/api/organizations/${safeId}/top-node`,
    );
    const top = response.data?.data?.topNodeOrganizationId;
    return top != null && String(top).trim() !== '' ? String(top) : null;
  },

  /**
   * Körjournalstandard från kundens toppnod (för förarschema).
   */
  getDrivingLogDefaults: async (
    organizationId: EntityId
  ): Promise<{ top_node_id: EntityId; driving_log_settings: DrivingLogSettings | null }> => {
    const safeId = assertPositiveIntId(organizationId, 'organizationId');
    const response = await api.get(`/api/organizations/${safeId}/driving-log-defaults`);
    return response.data.data;
  },

  /**
   * Hämta alla underorganisationer
   */
  getAllChildren: async (orgId: EntityId): Promise<Organization[]> => {
    const safeOrgId = assertPositiveIntId(orgId);
    const response = await api.get(`/api/organizations/${safeOrgId}/children`);
    return response.data.data;
  },

  /**
   * Hämta direkta barn
   */
  getDirectChildren: async (orgId: EntityId): Promise<Organization[]> => {
    const safeOrgId = assertPositiveIntId(orgId);
    const response = await api.get(`/api/organizations/${safeOrgId}/children/direct`);
    return response.data.data;
  },

  /**
   * Hämta organisation med hierarki
   */
  getOrganizationWithHierarchy: async (
    orgId: EntityId
  ): Promise<OrganizationWithChildren> => {
    const safeOrgId = assertPositiveIntId(orgId);
    const response = await api.get(`/api/organizations/${safeOrgId}/hierarchy`);
    return response.data.data;
  },

  /**
   * Skapa ny organisation
   */
  createOrganization: async (data: CreateOrganizationRequest): Promise<Organization> => {
    const response = await api.post('/api/organizations', data);
    return response.data.data;
  },

  /**
   * Uppdatera organisation
   */
  updateOrganization: async (
    orgId: EntityId,
    data: UpdateOrganizationRequest
  ): Promise<Organization> => {
    const safeOrgId = assertPositiveIntId(orgId);
    const response = await api.put(`/api/organizations/${safeOrgId}`, data);
    return response.data.data;
  },

  /**
   * Ta bort organisation
   */
  deleteOrganization: async (orgId: EntityId): Promise<{ deleteMode: 'soft' | 'hard' }> => {
    const safeOrgId = assertPositiveIntId(orgId);
    const response = await api.delete<{ success: boolean; data: { deleteMode: 'soft' | 'hard' } }>(
      `/api/organizations/${safeOrgId}`
    );
    return response.data.data;
  },

  /**
   * Flytta subträd
   */
  moveSubtree: async (orgId: EntityId, data: MoveOrganizationRequest): Promise<void> => {
    const safeOrgId = assertPositiveIntId(orgId);
    assertPositiveIntId(data.new_parent_id, 'new_parent_id');
    await api.post(`/api/organizations/${safeOrgId}/move`, data);
  },

  /**
   * Hämta aktiva bränslebolag (för dropdown)
   */
  getFuelCompanies: async (): Promise<{ id: number; name: string; logo_url: string | null }[]> => {
    const response = await api.get<{ success: boolean; data: { id: number; name: string; logo_url: string | null }[] }>(
      '/api/fuel-companies'
    );
    return response.data.data;
  },

  /**
   * Hämta bränslekonton (kundnummer per bränslebolag) för en organisation
   */
  getOrganizationFuelAccounts: async (
    organizationId: EntityId
  ): Promise<
    {
      id: number;
      fuel_company_id: number;
      customer_number: string;
      fuel_company_name?: string;
      fuel_company_logo_url?: string | null;
    }[]
  > => {
    const safeOrganizationId = assertPositiveIntId(organizationId);
    const response = await api.get<{
      success: boolean;
      data: {
        id: number;
        fuel_company_id: number;
        customer_number: string;
        fuel_company_name?: string;
        fuel_company_logo_url?: string | null;
      }[];
    }>(`/api/organizations/${safeOrganizationId}/fuel-accounts`);
    return response.data.data;
  },

  /**
   * Sätt bränslekonton för en organisation (ersätter befintliga)
   */
  setOrganizationFuelAccounts: async (
    organizationId: EntityId,
    fuelAccounts: { fuel_company_id: number; customer_number: string }[]
  ): Promise<{
    accounts: unknown[];
    linkedTransactions: number;
    unlinkedTransactions: number;
  }> => {
    const safeOrganizationId = assertPositiveIntId(organizationId);
    const response = await api.put<{
      success: boolean;
      data: unknown[];
      meta?: { linkedTransactions?: number; unlinkedTransactions?: number };
    }>(`/api/organizations/${safeOrganizationId}/fuel-accounts`, { fuelAccounts });
    return {
      accounts: response.data.data,
      linkedTransactions: Number(response.data.meta?.linkedTransactions ?? 0),
      unlinkedTransactions: Number(response.data.meta?.unlinkedTransactions ?? 0),
    };
  },
};

